import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, ChevronRight as Crumb } from "lucide-react";
import { QUOTES } from "./Fear";

const SLIDES = [
  { src: "/photos/grup-ziplama.jpg", title: "Gökyüzüne doğru", alt: "Sürücüler izlerken havada zıplayan enduro sürücüsü", wide: true },
  { src: "/photos/cam-ziplama.jpg", title: "Çamların arasında", alt: "Çamların önünde toz kaldırarak zıplayan enduro sürücüsü" },
  { src: "/photos/tepe-zirve.jpg", title: "Zirvede", alt: "Tepenin zirvesinde bekleyen enduro sürücüsü" },
  { src: "/photos/yokus-tirmanis.jpg", title: "Yokuşa meydan oku", alt: "Taşlı yokuşu tırmanan enduro sürücüsü" },
  { src: "/photos/toz-parkur.jpg", title: "Tozun içinden", alt: "Tozlu parkurda ilerleyen enduro sürücüsü" },
  { src: "/photos/gun-batimi.jpg", title: "Gün batımında", alt: "Gün batımında tepede enduro sürücüsü" },
  { src: "/photos/inis-toz.jpg", title: "Tozu dumana kat", alt: "Tozlu yokuştan inen enduro sürücüsü" },
  { src: "/photos/profil.jpg", title: "Tam gaz", alt: "Yokuşta yandan görünen enduro sürücüsü" },
  { src: "/photos/arka-tirmanis.jpg", title: "Zirveye son metreler", alt: "Kumlu yokuşu tırmanan enduro sürücüsü" },
  { src: "/photos/ay-yamac.jpg", title: "Mavi gökyüzü altında", alt: "Yamaçta ilerleyen enduro sürücüsü" },
  { src: "/photos/cam-grup.jpg", title: "Çamların gölgesinde", alt: "Çam ormanında motorlarıyla mola veren FX sürücüleri", wide: true },
  { src: "/photos/yan-surus.jpg", title: "Mavi ve toz", alt: "Tozlu parkurda yandan görünen enduro sürücüsü" },
  { src: "/photos/karsidan.jpg", title: "Göz göze", alt: "Parkurda kameraya doğru gelen enduro sürücüsü" },
  { src: "/photos/on-teker.jpg", title: "Ön teker havada", alt: "Ön tekeri havada yokuştan inen sürücü" },
];
const DELAY = 5500;

export const Showcase = () => {
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const touch = useRef(null);
  const go = useCallback((d) => setI((v) => (v + d + SLIDES.length) % SLIDES.length), []);

  useEffect(() => {
    if (paused) return undefined;
    const t = setTimeout(() => go(1), DELAY);
    return () => clearTimeout(t);
  }, [i, paused, go]);

  useEffect(() => {
    const k = (e) => {
      if (document.body.style.overflow === "hidden") return;
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [go]);

  const s = SLIDES[i];
  return (
    <section
      data-testid="gallery-showcase"
      className="relative h-[100svh] min-h-[560px] overflow-hidden bg-black"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={(e) => { touch.current = e.touches[0].clientX; }}
      onTouchEnd={(e) => {
        const dx = e.changedTouches[0].clientX - (touch.current ?? 0);
        if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
      }}
    >
      <AnimatePresence initial={false}>
        <motion.div key={s.src} className="absolute inset-0" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.9 }}>
          {!s.wide && <img src={s.src} alt="" aria-hidden className="absolute inset-0 hidden h-full w-full scale-110 object-cover opacity-50 blur-2xl lg:block" />}
          <motion.img
            src={s.src}
            alt={s.alt}
            data-testid="showcase-image"
            initial={{ scale: 1.08 }}
            animate={{ scale: 1 }}
            transition={{ duration: 6, ease: "easeOut" }}
            className={`absolute inset-0 h-full w-full ${s.wide ? "object-cover object-[50%_20%]" : "object-cover lg:object-contain"}`}
          />
        </motion.div>
      </AnimatePresence>
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black via-black/20 to-black/50" />

      <div className="absolute inset-x-0 bottom-0 z-10 mx-auto max-w-7xl px-5 pb-24 md:px-8 md:pb-16">
        <nav className="mb-5 flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-neutral-300">
          <Link to="/" className="hover:text-white" data-testid="breadcrumb-home">Ana Sayfa</Link><Crumb className="h-3 w-3" /><span className="text-white">Galeri</span>
        </nav>
        <p className="mb-3 flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.25em] text-neutral-200"><span className="h-[2px] w-8 bg-brand" />Parkurdan kareler</p>
        <AnimatePresence mode="wait">
          <motion.h1 key={s.title} data-testid="showcase-title" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.5 }} className="font-display text-6xl uppercase leading-none text-white md:text-8xl">
            {s.title}
          </motion.h1>
        </AnimatePresence>
        <AnimatePresence mode="wait">
          <motion.p key={`q-${i}`} data-testid="showcase-quote" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.5, delay: 0.15 }} className="mt-4 max-w-xl border-l-2 border-brand pl-4 text-base italic text-neutral-200 md:text-lg">
            {QUOTES[i % QUOTES.length]}
          </motion.p>
        </AnimatePresence>
        <div className="mt-8 flex items-center gap-6">
          <span data-testid="showcase-counter" className="font-display text-2xl tracking-wider text-white">{String(i + 1).padStart(2, "0")}<span className="text-neutral-500"> / {String(SLIDES.length).padStart(2, "0")}</span></span>
          <div className="flex flex-1 gap-1.5 md:max-w-xs">
            {SLIDES.map((x, k) => (
              <button key={x.src} onClick={() => setI(k)} aria-label={`Fotoğraf ${k + 1}`} data-testid={`showcase-dot-${k}`} className="h-1 flex-1 overflow-hidden bg-white/25">
                <span className={`block h-full bg-brand ${k === i ? (paused ? "w-full" : "animate-[grow_5.5s_linear_forwards]") : k < i ? "w-full" : "w-0"}`} />
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="absolute bottom-16 right-28 z-10 hidden gap-2 md:flex">
        <button onClick={() => go(-1)} aria-label="Önceki" data-testid="showcase-prev-btn" className="flex h-12 w-12 items-center justify-center border border-white/30 bg-black/30 text-white backdrop-blur-sm transition-colors hover:bg-white hover:text-black"><ChevronLeft className="h-6 w-6" /></button>
        <button onClick={() => go(1)} aria-label="Sonraki" data-testid="showcase-next-btn" className="flex h-12 w-12 items-center justify-center border border-white/30 bg-black/30 text-white backdrop-blur-sm transition-colors hover:bg-white hover:text-black"><ChevronRight className="h-6 w-6" /></button>
      </div>
    </section>
  );
};
